import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { portalApi, UserContext } from '../services/portal';

/**
 * MV5 Wave 1 · Cross-page user-context hook.
 *
 * Fetches GET /users/me/context once and caches on window for the
 * lifetime of the SPA. The session-manager service also 60s-caches on
 * its side, so a refetch across pages is cheap; but we still avoid the
 * network round-trip if the same value is on hand.
 *
 * Consumers get the parsed context, a loading flag, and an offline flag.
 * When we can't reach the server (typical demo scenario: local dev
 * without session-manager running), we return `null` context and an
 * `isOffline=true` flag so the Portal Home offline banner shows and the
 * page still renders instead of hanging on a spinner.
 *
 * Side-effect: sets `document.documentElement.dir` from context.rtl_flag.
 * That's the single-source-of-truth wiring the RTL constraint asks for.
 */

type Cached = { value: UserContext | null; fetchedAt: number; subject: string };
const CACHE_TTL_MS = 60_000;
const CACHE_KEY = '__hauraki_mv5_context_cache__';
let activeSubject: string | undefined;
let activeUserId: string | undefined;
let cacheGeneration = 0;

/** Account switches must remove cached learner data before another request. */
export function clearUserContext(): void {
  delete (window as Window & { [CACHE_KEY]?: Cached })[CACHE_KEY];
  activeSubject = undefined;
  activeUserId = undefined;
  cacheGeneration += 1;
}

function getCached(subject: string | undefined): Cached | null {
  const w = window as Window & { [CACHE_KEY]?: Cached };
  const c = w[CACHE_KEY];
  if (!c || !subject || c.subject !== subject) return null;
  if (Date.now() - c.fetchedAt > CACHE_TTL_MS) return null;
  return c;
}

function setCached(value: UserContext | null): void {
  if (!activeSubject) return;
  (window as Window & { [CACHE_KEY]?: Cached })[CACHE_KEY] = {
    value,
    fetchedAt: Date.now(),
    subject: activeSubject,
  };
}

/**
 * Replace the SPA's canonical context after a server-side mutation. This
 * prevents the onboarding gate from seeing its previous 60-second value when
 * the learner navigates directly to /portal after completing the flow.
 */
export function primeUserContext(value: UserContext): void {
  // A mutation started by the previous account may finish after a switch.
  // Only the local identity established by a fenced context read may prime.
  if (!activeSubject || !activeUserId || value.user_id !== activeUserId) return;
  setCached(value);
  if (typeof document !== 'undefined') {
    // Chrome direction follows the INTERFACE rtl flag (rtl_flag — the region's
    // site-language direction), NOT the content language. Picking an RTL content
    // language (Urdu/Punjabi/Pashto) must NOT mirror the whole portal — only the
    // content surfaces (e.g. CourseReader) flip, via content_rtl applied locally.
    document.documentElement.dir = value.rtl_flag ? 'rtl' : 'ltr';
    document.documentElement.lang = value.resolved_content_language ?? value.preferred_locale ?? 'en';
  }
}

/**
 * SIGNUP-GATE (Cluster D) · the shape the auth layer sends when a brand-new
 * student is blocked from provisioning because their region is waitlist/closed.
 *
 * The session-manager maps `SignupWaitlistedError` → HTTP 403 with a structured
 * body `{ error:'signup_waitlisted', country, region }` (the shared contract the
 * blocked-user screen renders). This is DISTINCT from a 401/403 "not signed in":
 * the ZewstID session is perfectly valid — the person simply isn't provisioned
 * yet, and MUST see the warm waitlist screen, never a bounce to /login and never
 * an empty portal. `country` is the TRUSTED Cloudflare edge country (CF-IPCountry)
 * the server resolved; `region` is the resolved region row (nullable).
 */
export interface SignupWaitlistBlock {
  country: string;
  region: string | null;
}

/**
 * Narrow an axios error body to the signup-waitlisted contract. Deliberately
 * strict: ONLY a 403 whose body literally carries `error:'signup_waitlisted'`
 * counts. Any other 403 (a real auth failure) stays on the unauthenticated
 * path, so this can never accidentally strand a signed-in learner on the
 * waitlist screen.
 */
function asSignupWaitlist(status: number | undefined, data: unknown): SignupWaitlistBlock | null {
  if (status !== 403 || !data || typeof data !== 'object') return null;
  const body = data as { error?: unknown; country?: unknown; region?: unknown };
  if (body.error !== 'signup_waitlisted') return null;
  return {
    country: typeof body.country === 'string' ? body.country : 'OTHER',
    region: typeof body.region === 'string' ? body.region : null,
  };
}

export interface UseUserContextResult {
  context: UserContext | null;
  isLoading: boolean;
  isOffline: boolean;
  isUnauthenticated: boolean;
  /**
   * Non-null when provisioning returned 403 signup_waitlisted. The app must
   * render the Waitlisted screen instead of signing the user into the portal.
   */
  waitlist: SignupWaitlistBlock | null;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useUserContext(): UseUserContextResult {
  const auth = useAuth();
  const subject = auth.isAuthenticated ? auth.user?.profile.sub : undefined;
  if (activeSubject !== subject) {
    clearUserContext();
    activeSubject = subject;
  }
  const cached = getCached(subject);
  const [contextSubject, setContextSubject] = useState(subject);
  const requestGeneration = useRef(0);
  const [context, setContext] = useState<UserContext | null>(cached?.value ?? null);
  const [isLoading, setIsLoading] = useState<boolean>(cached === null);
  const [error, setError] = useState<Error | null>(null);
  const [isOffline, setIsOffline] = useState<boolean>(false);
  const [isUnauthenticated, setIsUnauthenticated] = useState<boolean>(false);
  const [waitlist, setWaitlist] = useState<SignupWaitlistBlock | null>(null);

  const fetchContext = useCallback(async () => {
    const generation = ++requestGeneration.current;
    const epoch = cacheGeneration;
    const current = () => generation === requestGeneration.current && epoch === cacheGeneration && subject === activeSubject;
    setContextSubject(subject);
    setContext(null);
    setIsLoading(true);
    setError(null);
    setIsOffline(false);
    setIsUnauthenticated(false);
    setWaitlist(null);
    if (!subject) { setIsLoading(false); return; }
    try {
      const ctx = await portalApi.getContext();
      if (!current()) return;
      activeUserId = ctx.user_id;
      setContext(ctx);
      primeUserContext(ctx);
      // MV5 Wave 1 · RTL wiring — flip the document direction based on
      // the resolved user context. Marketing SA is the primary RTL locale;
      // any future locale flagged rtl_flag=true will pick this up.
      // Monaco + xterm stay LTR via the existing index.css [dir=rtl]
      // overrides, so switching direction here is safe.
    } catch (err) {
      if (!current()) return;
      // Distinguish the states the UI treats differently:
      //   403 signup_waitlisted → NEW student blocked by the region gate; show
      //                           the warm Waitlisted screen (NOT unauthenticated,
      //                           NOT offline). Checked FIRST because it is a 403.
      //   401/other 403 → unauthenticated (show "sign in" state, not offline)
      //   Network error / ECONNREFUSED → offline banner
      //   Anything else → error
      const asError = err as Error & {
        message?: string
        response?: { status?: number; data?: unknown }
      }
      const status = asError?.response?.status
      const message = asError?.message ?? ''
      const blocked = asSignupWaitlist(status, asError?.response?.data)
      if (blocked) {
        setWaitlist(blocked)
      } else if (status === 401 || status === 403) {
        setIsUnauthenticated(true)
      } else if (
        message === 'Network Error' ||
        message.includes('ECONNREFUSED') ||
        message.includes('Failed to fetch')
      ) {
        setIsOffline(true)
      } else {
        setError(asError)
      }
    } finally {
      if (current()) setIsLoading(false);
    }
  }, [subject]);

  useEffect(() => {
    const currentCache = getCached(subject);
    setContextSubject(subject);
    if (currentCache === null) {
      void fetchContext();
    } else if (typeof document !== 'undefined' && currentCache.value) {
      setContext(currentCache.value);
      setIsLoading(false);
      // Even from cache, re-assert the direction — a route change may have
      // happened before this effect ran and something else stomped on it.
      // Interface direction only (rtl_flag); content_rtl is applied per content surface.
      document.documentElement.dir = currentCache.value.rtl_flag ? 'rtl' : 'ltr';
      document.documentElement.lang = currentCache.value.resolved_content_language ?? currentCache.value.preferred_locale ?? 'en';
    }
    return () => { requestGeneration.current += 1; };
  }, [subject, fetchContext]);

  return {
    context: contextSubject === subject ? context : null,
    isLoading: contextSubject !== subject ? !!subject : isLoading,
    isOffline: contextSubject === subject && isOffline,
    isUnauthenticated: contextSubject === subject && isUnauthenticated,
    waitlist: contextSubject === subject ? waitlist : null,
    error: contextSubject === subject ? error : null,
    refetch: fetchContext,
  };
}
